// <Art>: responsive <picture> (AVIF + WebP srcset) for ids in public/art/manifest.json (§8.1).
import { signal } from '@preact/signals';

export interface ArtEntry {
  dir: string;
  widths: number[];
  width: number;
  height: number;
  role: string;
  alt: string;
  core: boolean;
}

export const artManifest = signal<Record<string, ArtEntry>>({});

export async function loadArtManifest(): Promise<void> {
  try {
    const r = await fetch('/art/manifest.json');
    if (r.ok) artManifest.value = ((await r.json()) as { images: Record<string, ArtEntry> }).images;
  } catch {
    /* offline before first cache: art falls back to its alt text */
  }
}

export function artUrl(id: string, width = 1280, ext: 'webp' | 'avif' = 'webp'): string | null {
  const a = artManifest.value[id];
  if (!a) return null;
  const w = a.widths.reduce((best, x) => (Math.abs(x - width) < Math.abs(best - width) ? x : best), a.widths[0]!);
  return `/${a.dir}/${id}-${w}.${ext}`;
}

export function Art(props: { id: string; sizes?: string; class?: string; alt?: string; decorative?: boolean; eager?: boolean }) {
  const a = artManifest.value[props.id];
  if (!a) return <span class={`art-missing ${props.class ?? ''}`} aria-hidden="true" />;
  const set = (ext: string) => a.widths.map((w) => `/${a.dir}/${props.id}-${w}.${ext} ${w}w`).join(', ');
  const largest = a.widths[a.widths.length - 1]!;
  return (
    <picture class={props.class}>
      <source type="image/avif" srcset={set('avif')} sizes={props.sizes ?? '100vw'} />
      <source type="image/webp" srcset={set('webp')} sizes={props.sizes ?? '100vw'} />
      <img
        src={`/${a.dir}/${props.id}-${largest}.webp`}
        alt={props.decorative ? '' : (props.alt ?? a.alt)}
        width={a.width}
        height={a.height}
        loading={props.eager ? 'eager' : 'lazy'}
        decoding="async"
      />
    </picture>
  );
}
